import SearchArea from "component/admin/search/searchArea";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import style from "css/admin/component.module.css";

import SearchDropdown from "component/admin/search/searchDropdown";
import SearchItem from "component/admin/search/searchItem";
import SearchRow from "component/admin/search/searchRow";
import SearchInput from "component/admin/search/searchInput";
import SearchButton from "component/admin/search/searchButton";
import { Button, Table, message } from "antd";
import axios from "axios";
import { ReactComponent as PlusIcon } from "asset/plus_icon.svg";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import ProductModal from "component/admin/modal/productModal";
import DeleteConfirmModal from "component/admin/modal/deleteConfirmModal";

function Product() {
    const [init, setInit] = useState(true);

    // 필터
    const [manufacturerList, setManufacturerList] = useState([{ key: "ALL", label: "전체" }]);
    const [manufacturerLoading, setManufacturerLoading] = useState(false);
    const [searchManufacturer, setSearchManufacturer] = useState("ALL");
    const [searchText, setSearchText] = useState("");

    // 데이터
    const [isLoading, setIsLoading] = useState(false);
    const [allProducts, setAllProducts] = useState([]);

    // 등록/수정(추후 구현) 모달
    const [registerModalOpen, setRegisterModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState("create"); // create | edit
    const [selectedRecord, setSelectedRecord] = useState(null);

    // 삭제 확인 모달
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // 제조사 dropdown 옵션 조회
    const getManufacturerList = useCallback(() => {
        setManufacturerLoading(true);
        const config = {
            params: {
                GROUP_CD: "CD03",
            },
        };
        axios
            .get("/code", config)
            .then((res) => {
                if (res.status === 200 && Array.isArray(res.data)) {
                    const items = [
                        { key: "ALL", label: "전체" },
                        ...res.data.map((row) => ({
                            key: row?.CODE_CD,
                            label: row?.VIEW_NM,
                        })),
                    ];
                    setManufacturerList(items);
                } else {
                    setManufacturerList([{ key: "ALL", label: "전체" }]);
                }
            })
            .catch(() => {
                setManufacturerList([{ key: "ALL", label: "전체" }]);
            })
            .finally(() => setManufacturerLoading(false));
    }, []);

    // 제품 목록 조회
    const getProductList = useCallback(() => {
        setIsLoading(true);

        const text = String(searchText || "").trim();
        const params = {};
        if (searchManufacturer !== "ALL") params.MANUFACTURER_CD = searchManufacturer;

        if (text) {
            const looksLikeCode = !/\s/.test(text) && /^[a-zA-Z0-9_/-]+$/.test(text);
            if (looksLikeCode) params.PRODUCT_CD = text;
            else params.PRODUCT_NM = text;
        }

        axios
            .get("/product", { params })
            .then((res) => {
                if (res.status === 200 && Array.isArray(res.data)) {
                    setAllProducts(res.data);
                } else {
                    setAllProducts([]);
                }
            })
            .catch(() => {
                setAllProducts([]);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [searchManufacturer, searchText]);

    useEffect(() => {
        if (init) {
            getManufacturerList();
            getProductList();
            setInit(false);
        }
    }, [getManufacturerList, getProductList, init]);

    const openCreateModal = useCallback(() => {
        setModalMode("create");
        setSelectedRecord(null);
        setRegisterModalOpen(true);
    }, []);

    const openEditModal = useCallback((record) => {
        setModalMode("edit");
        setSelectedRecord(record);
        setRegisterModalOpen(true);
    }, []);

    const deleteHandler = useCallback((record) => {
        setDeleteTarget(record || null);
        setDeleteModalOpen(true);
    }, []);

    const closeDeleteModal = useCallback(() => {
        if (deleteLoading) return;
        setDeleteModalOpen(false);
        setDeleteTarget(null);
    }, [deleteLoading]);

    const submitDelete = useCallback(async () => {
        const licenseId = deleteTarget?.LICENSE_ID;
        if (!licenseId) {
            message.error("삭제할 제품의 LICENSE_ID가 없습니다.");
            return;
        }
        try {
            setDeleteLoading(true);
            await axios.delete(`/product/${licenseId}`);
            message.success("삭제되었습니다.");
            closeDeleteModal();
            getProductList();
        } catch (e) {
            const msg = e?.response?.data ?? e?.message ?? "삭제 중 오류가 발생했습니다.";
            message.error(String(msg));
        } finally {
            setDeleteLoading(false);
        }
    }, [closeDeleteModal, deleteTarget, getProductList]);

    const closeModal = useCallback(() => {
        setRegisterModalOpen(false);
        setSelectedRecord(null);
    }, []);

    const dataSource = useMemo(() => allProducts || [], [allProducts]);

    const columns = useMemo(
        () => [
            {
                title: "제품 이미지",
                dataIndex: "IMG_NM",
                key: "IMG_NM",
                width: 110,
                align: "center",
                render: (_, record) => {
                    const prodPathNm = record?.MANUFACTURER_NM ? String(record.MANUFACTURER_NM).toLowerCase() : "";
                    const imgNm = record?.IMG_NM ? String(record.IMG_NM) : "";
                    const defaultSrc = `${process.env.REACT_APP_CLIENT_URL}icon/logo.jpg`;
                    const src = imgNm
                        ? `${process.env.REACT_APP_CLIENT_URL}img/product/${prodPathNm}/${imgNm}`
                        : defaultSrc;
                    const alt = record?.PRODUCT_NM ?? "Product";
                    return (
                        <img
                            src={src}
                            alt={alt}
                            className={style.product_table_img}
                            onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = defaultSrc;
                            }}
                        />
                    );
                },
            },
            { title: "제품명", dataIndex: "PRODUCT_NM", key: "PRODUCT_NM" },
            { title: "제품 코드", dataIndex: "PRODUCT_CD", key: "PRODUCT_CD", width: 160 },
            { title: "제조사", dataIndex: "MANUFACTURER_NM", key: "MANUFACTURER_NM", width: 180 },
            {
                title: "이미지 이름",
                dataIndex: "IMG_NM",
                key: "IMG_NM_TEXT",
                width: 180,
                render: (v) => (v == null || String(v).trim() === "" ? "-" : String(v)),
            },
            {
                title: "다운로드 링크",
                dataIndex: "DOWNLOAD_URL",
                key: "DOWNLOAD_URL",
                width: 420,
                render: (v) => {
                    const url = v == null ? "" : String(v).trim();
                    if (!url) return "-";
                    return (
                        <a
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                            title={url}
                            style={{ wordBreak: "break-all" }}
                        >
                            {url}
                        </a>
                    );
                },
            },
            {
                title: "라이선스 수량",
                dataIndex: "LICENSE_CNT",
                key: "LICENSE_CNT",
                width: 140,
                align: "center",
                render: (v) => (v == null ? "-" : Number(v).toLocaleString()),
            },
            {
                title: "",
                key: "actions",
                fixed: "center",
                width: 100,
                render: (_, record) => {
                    return (
                        <div style={{ display: 'flex', justifyContent: 'center', gap: 6 }}>
                            <Button
                                className={style.edit_btn}
                                type="text"
                                icon={<EditOutlined />}
                                aria-label="수정"
                                title="수정"
                                onClick={() => openEditModal(record)}
                            />
                            <Button
                                className={style.delete_btn}
                                type="default"
                                icon={<DeleteOutlined />}
                                aria-label="삭제"
                                title="삭제"
                                onClick={() => deleteHandler(record)}
                            />
                        </div>
                    );
                },
            },
        ],
        [deleteHandler, openEditModal]
    );

    return (
        <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 20 }}>
            <SearchArea title="제품 관리">
                <div className={style.searchItemArea}>
                    <SearchRow>
                        <SearchItem label="제조사">
                            <SearchDropdown
                                items={manufacturerList}
                                selectedKey={searchManufacturer}
                                loading={manufacturerLoading}
                                onChange={(key) => setSearchManufacturer(key)}
                                width={230}
                            />
                        </SearchItem>

                        <SearchItem>
                            <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
                                <SearchInput
                                    placeholder="제품명, 제품 코드 검색"
                                    width={260}
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') getProductList();
                                    }}
                                />
                                <SearchButton onClick={getProductList} disabled={isLoading}>
                                    조회
                                </SearchButton>
                            </div>
                        </SearchItem>
                    </SearchRow>
                </div>
            </SearchArea>

            <div className={style.content_wrapper}>
                <div className={style.content_container}>
                    <div className={style.header}>
                        <div className={style.text}>
                            전체 제품 (총 {Array.isArray(dataSource) ? dataSource.length : 0}건)
                        </div>
                        <div className={style.buttons}>
                            <Button className={style.register_btn} onClick={openCreateModal}>
                                <PlusIcon width={20} height={20} />
                                제품 추가
                            </Button>
                        </div>
                    </div>
                    <div className={style.content}>
                        <div style={{ marginTop: 16 }}>
                            <Table
                                rowKey={(row) => row?.LICENSE_ID ?? `${row?.MANUFACTURER_CD ?? ""}:${row?.PRODUCT_CD ?? ""}`}
                                columns={columns}
                                dataSource={dataSource}
                                size="middle"
                                pagination={{ pageSize: 10, showSizeChanger: false }}
                                scroll={{ x: 1550 }}
                                loading={isLoading}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <ProductModal
                isOpen={registerModalOpen}
                close={closeModal}
                mode={modalMode}
                initialData={selectedRecord}
                onSuccess={getProductList}
                manufacturerList={manufacturerList}
            />

            <DeleteConfirmModal
                open={deleteModalOpen}
                onCancel={closeDeleteModal}
                onConfirm={submitDelete}
                loading={deleteLoading}
                record={deleteTarget}
            />
        </div>
    );
}

export default Product;
